/**
 * The Files tab's preview pane (design "Flow Project Redesign", Files board): the selected file as an image, its
 * Markdown rendered, or its text, then its details (type, size, added, folder) and whether the agent reads it on its
 * own. A file it cannot show (a PDF, a zip, a text too large) says so and offers Download.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as vfsClient from '../../utils/vfsClient';
import { mimeTypeFor } from '../../utils/binaryFiles';
import { agentReads, previewKind } from '../../utils/filesModel';
import { ageOf, formatBytes } from '../home/FilesSection';
import MarkdownView from '../markdown/MarkdownView';

const KIND_LABEL = { input: 'Input', result: 'Result', brain: 'Brain', screenshot: 'Screenshot' };

/**
 * What a file shows as (`kind`, filesModel previewKind) and its content: `{ status: 'loading' | 'ready' | 'none' |
 * 'error', value?, error? }` (an image as an object URL, revoked when the file changes). Shared with the Files tab's file
 * view (FileView).
 */
export function useFileContent(projectId, row) {
  const kind = previewKind(row);
  const [content, setContent] = useState({ status: 'loading' });

  useEffect(() => {
    if (kind === 'none' || kind === 'large') { setContent({ status: 'none' }); return undefined; }
    let alive = true;
    let url = null;
    setContent({ status: 'loading' });
    const read = kind === 'image'
      ? vfsClient.readBinaryFile(projectId, row.path, mimeTypeFor(row.name)).then((blob) => {
        if (!blob) { throw new Error('The file is not there any more'); }
        url = URL.createObjectURL(blob);
        return url;
      })
      : vfsClient.readFile(projectId, row.path);
    read.then((value) => { if (alive) { setContent({ status: 'ready', value }); } })
      .catch((failure) => { if (alive) { setContent({ status: 'error', error: failure?.message || 'The file could not be read' }); } });
    return () => {
      alive = false;
      if (url) { URL.revokeObjectURL(url); }
    };
  }, [projectId, row.path, row.name, kind]);
  return { kind, content };
}

export default function FilePreview({ projectId, row, onDownload, onClose }) {
  const { kind, content } = useFileContent(projectId, row);
  const reads = agentReads(row);
  return (
    <aside aria-label={`Preview of ${row.name}`} className="files-preview" data-testid="files-preview">
      <div className="files-preview-head">
        <strong className="files-preview-name">{row.name}</strong>
        <button aria-label="Close the preview" className="files-link" onClick={onClose} type="button">Close</button>
      </div>
      <div className="files-preview-body">
        {content.status === 'loading' && <p className="files-hint">Loading the preview…</p>}
        {content.status === 'error' && <p className="files-hint" role="alert">The preview could not be read: {content.error}</p>}
        {content.status === 'none' && (
          <p className="files-hint">{kind === 'large' ? 'This file is too large to preview here.' : 'No preview for this kind of file.'} Download it to open it.</p>
        )}
        {content.status === 'ready' && kind === 'image' && <img alt={row.name} className="files-preview-image" src={content.value} />}
        {content.status === 'ready' && kind === 'markdown' && (
          <MarkdownView className="files-preview-md" filePath={row.path} projectId={projectId} source={content.value} testId="files-preview-md" />
        )}
        {content.status === 'ready' && kind === 'text' && <pre className="files-preview-text">{content.value}</pre>}
      </div>
      <dl className="files-preview-facts">
        <dt>Type</dt><dd>{KIND_LABEL[row.kind]}</dd>
        <dt>Size</dt><dd>{formatBytes(row.size) || '—'}</dd>
        <dt>Added</dt><dd>{ageOf(row.modified) || '—'}</dd>
        <dt>Folder</dt><dd>{row.dir}</dd>
        <dt>Agent can read it</dt>
        <dd data-testid="files-agent-reads">{reads ? 'Yes, it reads the Brain before it works' : 'Only when asked: it is outside the Brain'}</dd>
      </dl>
      <button className="files-btn" onClick={() => onDownload(row)} type="button">Download</button>
    </aside>
  );
}

FilePreview.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  row: PropTypes.shape({
    dir: PropTypes.string, kind: PropTypes.string, modified: PropTypes.string, name: PropTypes.string, path: PropTypes.string, size: PropTypes.number,
  }).isRequired,
};
